/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-26
 */

// Add third-party dependencies.
import { useState } from 'react';
import {
  Button,
  Menu,
  MenuItem,
  Box,
  Typography,
  Divider,
  ListItemIcon,
  ListItemText,
  Alert,
  Snackbar,
  CircularProgress,
} from '@mui/material';
import {
  Logout,
  Dashboard,
  Lock,
  Person,
  CreditCard,
  Settings,
  Upgrade as UpgradeIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

// Add local dependencies.
import { useGetCurrentSubscriptionQuery } from '../../../features/auth';
import type { RootState } from '../../../store';
import { getSafeDisplayName } from '../../../utils/security';
import UserAvatar from '../../common/UserAvatar';
import { useSubscriptionLimits } from '../../../hooks/useSubscriptionLimits';
import { useTrialStatus } from '../../../hooks/useTrialStatus';
import { TrialCountdown } from '../../subscription/TrialCountdown';
import { TrialBadge } from '../../subscription/TrialBadge';
import { useSignOut } from '../../../hooks/useSignOut';

/**
 * Props for the UserMenu component.
 */
interface UserMenuProps {
  /**
   * Additional CSS classes.
   */
  className?: string;
}

/**
 * Enhanced user menu component for authenticated users.
 * Provides organized access to user account, studio tools, and system functions.
 *
 * @param props - The component props
 * @returns {React.ReactElement} The user menu component
 *
 * @example
 * return (
 *   <UserMenu />
 * );
 */
const UserMenu: React.FC<UserMenuProps> = ({ className }) => {
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => state.auth.user);
  const { signOut, isSigningOut, signOutError, setSignOutError } = useSignOut();

  // Subscription data
  const { data: subscription, isLoading: subscriptionLoading } =
    useGetCurrentSubscriptionQuery();
  const {
    remainingStudios,
    isAtStudioLimit,
    isLoading: limitsLoading,
  } = useSubscriptionLimits();

  // Trial status data
  const {
    isTrialActive,
    daysRemaining,
    planName: trialPlanName,
  } = useTrialStatus();

  // Menu state
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleMenuClose();
    await signOut();
  };

  const handleProfileClick = () => {
    handleMenuClose();
    navigate('/profile');
  };

  const handleChangePasswordClick = () => {
    handleMenuClose();
    navigate('/profile?mode=change-password');
  };

  const handleSettingsClick = () => {
    handleMenuClose();
    navigate('/settings');
  };

  const handleDashboardClick = () => {
    handleMenuClose();
    navigate('/dashboard');
  };

  const handleBillingClick = () => {
    handleMenuClose();
    navigate('/billing');
  };

  const handleUpgradeClick = () => {
    handleMenuClose();
    navigate('/pricing');
  };

  if (!user) {
    return null;
  }

  return (
    <>
      <Box
        className={className}
        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
      >
        <Typography
          variant="body2"
          sx={{ color: 'inherit', display: { xs: 'none', sm: 'block' }, ml: 2 }}
        >
          Hi, {getSafeDisplayName(user?.first_name, user?.email)}
        </Typography>

        {/* Trial Badge */}
        <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
          <TrialBadge
            size="small"
            clickable
            onClick={() => handleMenuOpen({} as React.MouseEvent<HTMLElement>)}
          />
        </Box>

        <Button
          onClick={handleMenuOpen}
          sx={{
            color: 'inherit',
            minWidth: 'auto',
            p: 1,
            borderRadius: '50%',
          }}
          disabled={isSigningOut}
          aria-label="Open user menu"
        >
          {isSigningOut ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            <UserAvatar user={user} size="small" clickable />
          )}
        </Button>
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        onClick={handleMenuClose}
        PaperProps={{
          elevation: 3,
          sx: {
            mt: 1.5,
            minWidth: 220,
            '& .MuiMenuItem-root': {
              px: 2,
              py: 1,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {/* Account Section */}
        <MenuItem onClick={handleProfileClick}>
          <ListItemIcon>
            <Person fontSize="small" />
          </ListItemIcon>
          <ListItemText>Profile</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleChangePasswordClick}>
          <ListItemIcon>
            <Lock fontSize="small" />
          </ListItemIcon>
          <ListItemText>Change Password</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleSettingsClick}>
          <ListItemIcon>
            <Settings fontSize="small" />
          </ListItemIcon>
          <ListItemText>Settings</ListItemText>
        </MenuItem>

        <Divider />

        {/* Studio Tools Section */}
        <MenuItem onClick={handleDashboardClick}>
          <ListItemIcon>
            <Dashboard fontSize="small" />
          </ListItemIcon>
          <ListItemText>Dashboard</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleBillingClick}>
          <ListItemIcon>
            <CreditCard fontSize="small" />
          </ListItemIcon>
          <ListItemText>Billing & Subscription</ListItemText>
        </MenuItem>

        {/* Subscription Info Section */}
        <Divider />

        <Box sx={{ px: 2, py: 1 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontWeight: 600 }}
          >
            Subscription
          </Typography>
          <Box sx={{ mt: 0.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {subscription?.plan?.name || 'Free'} Plan
            </Typography>
            {!subscriptionLoading && !limitsLoading && subscription?.plan && (
              <>
                {subscription.plan.max_owned_studios > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    {(() => {
                      if (isAtStudioLimit) return 'At studio limit';
                      if (remainingStudios === Infinity)
                        return 'Unlimited studios';
                      if (
                        typeof remainingStudios === 'number' &&
                        !Number.isNaN(remainingStudios)
                      ) {
                        return `${remainingStudios} studio${remainingStudios !== 1 ? 's' : ''} remaining`;
                      }
                      return 'Loading...';
                    })()}
                  </Typography>
                )}
                {subscription.plan.max_owned_studios === 0 && (
                  <Typography variant="caption" color="text.secondary">
                    Unlimited studios
                  </Typography>
                )}
              </>
            )}
            {(subscriptionLoading || limitsLoading) && (
              <Typography variant="caption" color="text.secondary">
                Loading...
              </Typography>
            )}
          </Box>
        </Box>

        {/* Trial Status Section */}
        {isTrialActive && (
          <Box sx={{ px: 2, py: 1 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 600 }}
            >
              Trial Status
            </Typography>
            <Box sx={{ mt: 0.5 }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {trialPlanName} Trial - {daysRemaining} day
                {daysRemaining !== 1 ? 's' : ''} left
              </Typography>
              <Box sx={{ mt: 1 }}>
                <TrialCountdown variant="progress" showDetails={false} />
              </Box>
            </Box>
          </Box>
        )}

        <MenuItem onClick={handleUpgradeClick}>
          <ListItemIcon>
            <UpgradeIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary={isTrialActive ? 'Upgrade Now' : 'Upgrade Plan'}
            secondary={isAtStudioLimit ? 'At limit' : undefined}
          />
        </MenuItem>

        <Divider />

        {/* System Section */}
        <MenuItem onClick={handleLogout} disabled={isSigningOut}>
          <ListItemIcon>
            <Logout fontSize="small" />
          </ListItemIcon>
          <ListItemText>
            {isSigningOut ? 'Signing out...' : 'Sign Out'}
          </ListItemText>
        </MenuItem>
      </Menu>

      {/* Sign-out error (a successful sign-out reloads the app) */}
      <Snackbar
        open={!!signOutError}
        autoHideDuration={6000}
        onClose={() => setSignOutError('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSignOutError('')}
          severity="error"
          sx={{ width: '100%' }}
        >
          {signOutError}
        </Alert>
      </Snackbar>
    </>
  );
};

export default UserMenu;
